import React, {ReactNode} from "react";
import IconPlaylistPlay from "@mui/icons-material/PlaylistPlay";
import {Tooltip} from "@mui/material";
import {DeviceActiveResource, DeviceScheduleStatus} from "@barix/rpp-types";
import ActiveResourceMain from "../ActiveResourceMain";
import ActiveResourceTracks from "../ActiveResourceTracks";
import ActiveResourceEvent from "../ActiveResourceEvent";
import ActiveResourceUsb from "../ActiveResourceUsb";
import ActiveResourcePriority from "../ActiveResourcePriority";
import ActiveResourceNone from "../ActiveResourceNone";
import ActiveResourceFallback from "../ActiveResourceFallback";
import ActiveResourceWarning from "../ActiveResourceWarning";
import ActiveResourceUnknown from "../ActiveResourceUnknown";
import RppColors from "../../RppColors";


class DevicePlaying extends React.Component<{
    device: {
        online: boolean;
        status: {
            activeResource: DeviceActiveResource;
            activeStreamName?: string;
            activeStream: string;
            scheduleStatus: DeviceScheduleStatus;
        }
    } | undefined,
    activeResourceI18N: string;
    style?: React.CSSProperties;
}, {}> {


    private static getIconPlayingStyle(
        device: {
            online: boolean;
            status: {
                activeResource: DeviceActiveResource;
                scheduleStatus: DeviceScheduleStatus;
            }
        } | undefined): React.CSSProperties {

        // "NONE"; // "PRIORITY"; // "MAIN"; // "FALLBACK"; // "EVENT"; // "LOCAL_USB"; // "FAILOVER_TRACKS";

        if (device && device.online && device.status && device.status.scheduleStatus !== "Inactive") {
            switch (device.status.activeResource) {
                case "MAIN":
                    return {color: RppColors.deviceActiveResourceMain()};
                case "FALLBACK":
                    return {color: RppColors.deviceActiveResourceFallbackUrl()};
                case "FAILOVER_TRACKS":
                    return {color: RppColors.deviceActiveResourceFailoverTrack()};
                case "EVENT":
                    return {color: RppColors.deviceActiveResourceEvent()};
                case "LOCAL_USB":
                    return {color: RppColors.deviceActiveResourceLocalUsb()};
                case "PRIORITY":
                    return {color: RppColors.deviceActiveResourcePriority()};
                case "NONE":
                    return {color: RppColors.deviceActiveResourceNone()};
                default:
                    return {color: RppColors.deviceActiveResourceUndefined()};
            }
        }
        return {color: RppColors.deviceActiveResourceUndefined()};
    }


    private static getIconPlayingTitle(
        device: {
            online: boolean;
            status: {
                activeResource: DeviceActiveResource;
                activeStream: string;
                activeStreamName?: string;
            }
        } | undefined,
        activeResourceI18N: string,
    ): NonNullable<React.ReactNode> {

        let prefix = "";
        if (device && device.status) {
            if (!device.online) {
                prefix = "Last playing: ";
            } else {
                prefix = "Playing: ";
            }

            const deviceSupportActiveStream = this.deviceSupportFeature(device && device.status ? device.status.activeResource : null);

            return (
                <React.Fragment>
                    {deviceSupportActiveStream && (
                        <React.Fragment>
                            <span>{prefix + activeResourceI18N}</span><br/>
                            <span>{device.status.activeStreamName ? device.status.activeStreamName : device.status.activeStream}</span>
                        </React.Fragment>
                    )}
                    {!deviceSupportActiveStream && (
                        <span>{"Device firmware does not support this feature. Please update your device firmware."}</span>
                    )}
                </React.Fragment>
            );
        }
        return "";
    }


    private static deviceSupportFeature(activeResource: DeviceActiveResource)   {
        switch (activeResource) {
            case "MAIN":
            case "FAILOVER_TRACKS":
            case "FALLBACK":
            case "EVENT":
            case "LOCAL_USB":
            case "PRIORITY":
            case "NONE":
                return true;
            default:
                return false;
        }
    }


    private renderIcon(
        device: {
            online: boolean;
            status: {
                activeResource: DeviceActiveResource;
                scheduleStatus: DeviceScheduleStatus;
            }
        } | undefined): ReactNode    {

        const iconStyle = {fontSize: "1em", ...this.props.style};
        if (device && device.online && device.status && device.status.scheduleStatus !== "Inactive") {
            switch (device.status.activeResource) {
                case "MAIN":
                    return (<ActiveResourceMain style={iconStyle}/>);
                case "FAILOVER_TRACKS":
                    return (<ActiveResourceTracks style={iconStyle}/>);
                case "FALLBACK":
                    return (<ActiveResourceFallback style={iconStyle}/>);
                case "EVENT":
                    return (<ActiveResourceEvent style={iconStyle}/>);
                case "LOCAL_USB":
                    return (<ActiveResourceUsb style={iconStyle}/>);
                case "PRIORITY":
                    return (<ActiveResourcePriority style={iconStyle}/>);
                case "NONE":
                    return (<ActiveResourceNone style={iconStyle}/>);
                default:
            }
        }
        return (<ActiveResourceUnknown style={iconStyle}/>);
    }


    public render(): ReactNode {
        if (this.props.device) {

            const deviceSupportActiveStream = !this.props.device.online || DevicePlaying.deviceSupportFeature(this.props.device && this.props.device.status ? this.props.device.status.activeResource : null );

            return (
                <Tooltip
                    title={DevicePlaying.getIconPlayingTitle(this.props.device, this.props.activeResourceI18N)}>
                    <div style={{ lineHeight: "1", fontSize: "0.75em", ...DevicePlaying.getIconPlayingStyle(this.props.device)}}>
                        {/*div is wrapper to hold css color*/}
                        {deviceSupportActiveStream && (
                            this.renderIcon(this.props.device)
                        )}
                        {!deviceSupportActiveStream && (
                            <ActiveResourceWarning/>
                        )}
                    </div>
                </Tooltip>
            );
        } else {
            return (<IconPlaylistPlay/>);
        }
    }

}


export default DevicePlaying;
